import { printReceiptViaApi } from '../data/api/receiptPrinterApiClient';
import {
  printThermalReceipt,
  RECEIPT_PRINTER_NOT_CONFIGURED,
} from './receiptPrintClient';
import { readReceiptPrinterConfigStorage } from './receiptPrinterStorage';
import type { ThermalReceiptData } from './thermalReceiptTypes';

export type ReceiptPrintResult =
  | { ok: true; mode: 'api' | 'electron'; printer?: string }
  | { ok: false; code: string; error: string };

/**
 * Silent print to the kiosk-bound receipt printer (never opens a picker dialog).
 * Printer binding lives in browser/Electron localStorage only; each print sends
 * `printerName` to the kiosk API/IPC (not persisted remotely).
 */
export async function printSaleReceipt(
  data: ThermalReceiptData,
  accessToken: string,
): Promise<ReceiptPrintResult> {
  const stored = readReceiptPrinterConfigStorage();
  const printerName = stored?.printerName;
  const paperSize = stored?.paperSize;

  // Electron / local OS queues first (Windows USB thermals like Aclas are not on CUPS).
  const electron = await printThermalReceipt(data, printerName, paperSize);
  if (electron.ok) {
    return { ok: true, mode: 'electron', printer: electron.printer };
  }

  let apiError: ReceiptPrintResult | null = null;
  if (accessToken) {
    const viaApi = await printReceiptViaApi(accessToken, data, printerName, paperSize);
    if (viaApi.ok) {
      return { ok: true, mode: 'api', printer: viaApi.printer };
    }
    apiError = viaApi;
  }

  if (
    apiError?.code === RECEIPT_PRINTER_NOT_CONFIGURED ||
    electron.code === RECEIPT_PRINTER_NOT_CONFIGURED
  ) {
    return {
      ok: false,
      code: RECEIPT_PRINTER_NOT_CONFIGURED,
      error: RECEIPT_PRINTER_NOT_CONFIGURED,
    };
  }

  return apiError ?? electron;
}

export function isReceiptPrinterConfigured(): boolean {
  return !!readReceiptPrinterConfigStorage()?.printerName;
}

export { RECEIPT_PRINTER_NOT_CONFIGURED };
